import * as Sentry from '@sentry/react';
import { skipToken, useQuery } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdPricingEvents,
  GetV1CompaniesCompanyIdPricingEventsData,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { PricingEvent } from '~hooks/usePricing/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Runaway guard, not a product limit. The loop trusts the server's Link header to stop
// advertising a next page; a server that repeated a cursor would otherwise spin the browser
// against the API indefinitely. 50 pages is 5,000 records — well past the largest realistic
// order (a 20-job / 200-load order with two rate edits is ~5 pages), so reaching it means a
// bug worth a Sentry report rather than a big order.
const PRICING_EVENTS_MAX_PAGES = 50;

type PricingEventsQuery = NonNullable<GetV1CompaniesCompanyIdPricingEventsData['query']>;

interface UsePricingEventsQueryProps {
  /** Polymorphic subject, e.g. 'Load' | 'Order' | 'Job' | 'JobAssignment'. */
  subjectType?: string;
  subjectId?: Nullable<string>;
  /**
   * Job-grain timeline: the Job plus its JobAssignments and Loads, in one query
   * (the FE collapses by action_id). Takes precedence over subjectType/subjectId.
   */
  jobId?: Nullable<string>;
  /**
   * Order-grain timeline: every event denormalized to this Order — the Order plus
   * all of its Jobs, JobAssignments, and Loads — in one query (the FE collapses by
   * action_id). `jobId` takes precedence; otherwise this wins over subjectType/subjectId.
   */
  orderId?: Nullable<string>;
}

const pricingEventsQueryKey = (
  companyId: string,
  { subjectType, subjectId, jobId, orderId }: UsePricingEventsQueryProps,
) => ['pricingEvents', companyId, { subjectType, subjectId, jobId, orderId }] as const;

/**
 * Fetches `GET /v1/companies/{company-id}/pricing_events` for the `load_level_pricing` feature:
 * an entity timeline (`subject_type`/`subject_id`), the Job-grain timeline (`job_id`, the job's own Job/JA/Load
 * events), or the Order-grain timeline (`order_id`, the whole Order subtree). `order_id` and the
 * `subject_*` filters are typed by the spec.
 *
 * Follows the cursor to the end and returns the WHOLE history, like the sibling Job and Project
 * timelines (`useJob.getAllJobEvents`, `useProjectEvents.getAllProjectEvents`). Partial history is
 * not an option here: one rate change writes a record per entity it touched — the Order, every
 * JobAssignment, every Load — and the panel collapses all of them into ONE entry by `action_id`.
 * A page of records is therefore not a page of entries (a ~90-record edit renders as a single
 * row), so stopping early both hides history and blanks the rate-type unit on any value-only
 * change whose type-setting event was left behind (`resolveEffectiveRateTypes`).
 */
export default function usePricingEventsQuery({
  subjectType = 'Load',
  subjectId,
  jobId,
  orderId,
}: UsePricingEventsQueryProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const filter: PricingEventsQuery = jobId
    ? { job_id: jobId }
    : orderId
      ? { order_id: orderId }
      : { subject_type: subjectType, subject_id: subjectId ?? undefined };

  return useQuery({
    queryKey: pricingEventsQueryKey(companyId, {
      subjectType,
      subjectId,
      jobId,
      orderId,
    }),
    queryFn:
      companyId && (jobId || orderId || subjectId)
        ? async ({ signal }) => {
            const events: PricingEvent[] = [];
            let cursor: string | undefined;
            let hasMorePages = true;
            let pagesFetched = 0;

            try {
              while (hasMorePages && pagesFetched < PRICING_EVENTS_MAX_PAGES) {
                const query: PricingEventsQuery = {
                  ...filter,
                  'page[limit]': MAX_API_PAGE_SIZE,
                  ...(cursor ? { 'page[after]': cursor } : {}),
                };
                const response = await getV1CompaniesCompanyIdPricingEvents({
                  path: { 'company-id': companyId },
                  query,
                  signal,
                });

                events.push(...response.data.data.map(PricingEvent.parse));

                cursor = extractPagination(response).after;
                hasMorePages = Boolean(cursor);
                pagesFetched += 1;
              }

              if (hasMorePages) {
                Sentry.captureMessage('PricingEvents: page cap hit, history truncated', {
                  level: 'error',
                  extra: { subjectType, subjectId, jobId, orderId, pagesFetched },
                });
              }
            } catch (error) {
              connection.handleRequestError(
                error,
                t('error_messages.load.failed_to_fetch_pricing_events'),
              );
              throw error;
            }

            return events;
          }
        : skipToken,
  });
}
